/**
 * Enhanced API Client with Intelligent Caching
 * Implements cache-first, stale-while-revalidate, and network-first strategies
 * Integrates with cache manager for automatic request optimization
 */

import axios from 'axios';
import { getCacheManager } from './cache-manager';
import { getCacheConfig, getCacheKey } from './cache-config';
import { getOfflineManager } from './offline';

class CachedAPIClient {
  constructor(baseURL, config = {}) {
    this.cacheManager = getCacheManager();
    this.baseURL = baseURL;

    this.axiosInstance = axios.create({
      baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    // Request interceptor for auth and cache handling
    this.axiosInstance.interceptors.request.use((config) => {
      // Add auth token if available
      const token = this.getAuthToken?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Mark request for cache tracking
      config.meta = config.meta || {};
      config.meta.startTime = Date.now();
      config.meta.cacheGeneration = this.cacheManager.getGeneration();

      return config;
    });

    // Response interceptor for cache updates and error handling
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Update cache with fresh data
        const cacheConfig = getCacheConfig(response.config.url);
        if (response.status === 200 && cacheConfig.cacheDuration > 0) {
          this.cacheManager.set(response.config.url, response.config.params || {}, response.data, {
            etag: response.headers.etag,
            apiVersion: response.headers['api-version'],
            cacheGeneration: response.config.meta.cacheGeneration,
          });
        }

        // Add response metadata
        response.meta = {
          cached: false,
          source: 'network',
          duration: Date.now() - response.config.meta.startTime,
        };

        // Notify OfflineManager of success
        getOfflineManager().reportSuccess();

        return response;
      },
      (error) => {
        // Handle 401 - token expired
        if (error.response?.status === 401) {
          this.onAuthError?.();
        }

        // Handle network errors (no response from server)
        if (!error.response) {
          getOfflineManager().reportFailure();

          if (this.shouldFallbackToCache(error.config)) {
            return this.getFallbackResponse(error.config);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  get interceptors() {
    return this.axiosInstance.interceptors;
  }

  /**
   * Enhanced GET request with intelligent caching
   */
  async get(url, config = {}) {
    const cacheConfig = getCacheConfig(url);
    const params = config.params || {};
    const key = getCacheKey(url, params);

    // Handle different cache strategies
    switch (cacheConfig.strategy) {
      case 'cache-first':
        return this.cacheFirstStrategy(url, config, cacheConfig);

      case 'stale-while-revalidate':
        return this.staleWhileRevalidateStrategy(url, config, cacheConfig);

      case 'network-first':
      default:
        return this.networkFirstStrategy(url, config, cacheConfig);
    }
  }

  /**
   * Cache-First Strategy
   * Return cached data immediately, fetch fresh only if expired
   */
  async cacheFirstStrategy(url, config, cacheConfig) {
    const params = config.params || {};
    const cached = await this.cacheManager.get(url, params);

    if (cached.data) {
      // Return cached response
      const response = {
        data: cached.data,
        status: 200,
        statusText: 'OK (Cached)',
        headers: { 'x-cache': cached.source },
        config,
        meta: {
          cached: true,
          source: cached.source,
          duration: 0,
        },
      };

      // Refresh in background if stale
      if (this.isStale(url, params)) {
        this.cacheManager.queueBackgroundUpdate(url, params, () =>
          this.axiosInstance.get(url, config).then(res => {
            // Reject if this is a cache fallback from the error interceptor
            if (res.meta?.source === 'offline-fallback') {
              return Promise.reject(new Error('Offline fallback, skip cache update'));
            }
            return res.data;
          }),
        );
      }

      return response;
    }

    // Cache miss - fetch fresh
    return this.axiosInstance.get(url, config);
  }

  /**
   * Stale-While-Revalidate Strategy
   * Return cached data immediately and fetch fresh in background
   */
  async staleWhileRevalidateStrategy(url, config, cacheConfig) {
    const params = config.params || {};
    const cached = await this.cacheManager.get(url, params);

    if (cached.data) {
      // Return cached response immediately
      const cacheResponse = {
        data: cached.data,
        status: 200,
        statusText: 'OK (Cached)',
        headers: { 'x-cache': cached.source },
        config,
        meta: {
          cached: true,
          source: cached.source,
          duration: 0,
          isStale: this.isStale(url, params),
        },
      };

      // Always revalidate in background
      this.cacheManager.queueBackgroundUpdate(url, params, () =>
        this.axiosInstance.get(url, config).then(res => {
          // Reject if this is a cache fallback from the error interceptor
          if (res.meta?.source === 'offline-fallback') {
            return Promise.reject(new Error('Offline fallback, skip cache update'));
          }
          return res.data;
        }),
      );

      return cacheResponse;
    }

    // Cache miss - fetch fresh and cache
    return this.axiosInstance.get(url, config);
  }

  /**
   * Network-First Strategy
   * Try network first, fallback to cache only on failure
   */
  async networkFirstStrategy(url, config, cacheConfig) {
    try {
      const response = await this.axiosInstance.get(url, config);
      response.meta.cached = false;
      response.meta.source = 'network';
      return response;
    } catch (error) {
      // Try cached data as fallback
      const params = config.params || {};
      const cached = await this.cacheManager.get(url, params);

      if (cached.data) {
        return {
          data: cached.data,
          status: 200,
          statusText: 'OK (Cached Fallback)',
          headers: { 'x-cache': 'fallback' },
          config,
          meta: {
            cached: true,
            source: 'cached-fallback',
            duration: 0,
            error: error.message,
          },
        };
      }

      throw error;
    }
  }

  /**
   * POST request (no caching)
   */
  async post(url, data = {}, config = {}) {
    const response = await this.axiosInstance.post(url, data, config);
    response.meta = {
      cached: false,
      source: 'network',
      duration: Date.now() - response.config.meta.startTime,
    };

    // Invalidate related cache on mutation
    this.invalidateRelated(url);

    return response;
  }

  /**
   * PUT request (no caching)
   */
  async put(url, data = {}, config = {}) {
    const response = await this.axiosInstance.put(url, data, config);
    this.invalidateRelated(url);
    return response;
  }

  /**
   * PATCH request (no caching)
   */
  async patch(url, data = {}, config = {}) {
    const response = await this.axiosInstance.patch(url, data, config);
    this.invalidateRelated(url);
    return response;
  }

  /**
   * DELETE request (no caching)
   */
  async delete(url, config = {}) {
    const response = await this.axiosInstance.delete(url, config);
    this.invalidateRelated(url);
    return response;
  }

  /**
   * Check if cache is stale
   */
  isStale(url, params = {}) {
    const config = getCacheConfig(url);
    const key = getCacheKey(url, params);

    // Check memory cache entry for timestamp
    const memEntry = this.cacheManager.memory?.cache?.get(key);
    if (!memEntry) return true;

    const age = Date.now() - memEntry.timestamp;
    const staleThreshold = config.staleTime || config.cacheDuration;
    return age > staleThreshold;
  }

  /**
   * Should fallback to cache on error
   */
  shouldFallbackToCache(config) {
    return config.method?.toUpperCase() === 'GET';
  }

  /**
   * Get fallback cached response
   */
  async getFallbackResponse(config) {
    const cached = await this.cacheManager.get(config.url, config.params || {});

    if (cached.data) {
      return {
        data: cached.data,
        status: 200,
        statusText: 'OK (Offline)',
        headers: { 'x-cache': 'offline' },
        config,
        meta: {
          cached: true,
          source: 'offline-fallback',
        },
      };
    }

    // Notify OfflineManager of cache miss while offline
    getOfflineManager().notifyCacheMiss(config.url);
    return Promise.reject(new Error('No cached data available'));
  }

  /**
   * Invalidate cache for related endpoints
   */
  invalidateRelated(url) {
    const baseEndpoint = url.split('?')[0].split('/').slice(0, -1).join('/');
    this.cacheManager.invalidate(baseEndpoint);
  }

  /**
   * Clear all cache
   */
  async clearCache() {
    await this.cacheManager.clearAll();
  }

  /**
   * Get cache stats
   */
  async getCacheStats() {
    return this.cacheManager.getStats();
  }

  /**
   * Set auth token getter (inject from your auth service)
   */
  setAuthTokenGetter(fn) {
    this.getAuthToken = fn;
  }

  /**
   * Set auth error handler (inject from your auth service)
   */
  setAuthErrorHandler(fn) {
    this.onAuthError = fn;
  }
}

export default CachedAPIClient;
